import React from 'react';
import { useTranslation } from 'react-i18next';
import { LeagueSeasonMatches } from '@/components/LeagueSeasonMatches';
import { MainSection } from '@/components/layout/MainSection';
import { useLeagueDetailContext } from '@/layouts/LeagueDetailLayout/context';
import { useLeagueMatches } from '@/hooks/useLeagueMatches';
import styles from '../LeagueDetailPage.module.scss';

/**
 * `/matches/:leagueId` — the league's whole season on one page scroll
 * (`LeagueSeasonMatches`: Date / Round views). Rows link to `/match/:id`.
 *
 * Live updates: `useLeagueMatches` polls while a match is live or about to
 * kick off; between polls `useLiveTick` inside the row extrapolates the minute.
 */
const MatchesTab: React.FC = () => {
  const { league, leagueId } = useLeagueDetailContext();
  const { t } = useTranslation(['league']);

  const { data, isLoading, error } = useLeagueMatches(leagueId);

  const matches = data?.data;

  let body: React.ReactNode;
  if (error) {
    body = <p className={styles.error}>{t('league:errorLoadingMatches')}</p>;
  } else if (isLoading || !matches) {
    body = null;
  } else if (matches.length === 0) {
    body = (
      <div className={styles.emptyState} data-testid="league-matches-empty">
        <p className={styles.emptyStateText}>{t('league:noMatchesInLeague')}</p>
      </div>
    );
  } else {
    // Keyed by league: in-place league → league navigation starts a fresh
    // list (mode from the new history entry, new anchor).
    body = <LeagueSeasonMatches key={leagueId} leagueId={leagueId} matches={matches} />;
  }

  return (
    <MainSection
      subtitleHideOnMobile
      title={league.name}
      subtitle={t('league:matches')}
      logoUrl={league.logoUrl || undefined}
      logoAlt={league.name}
      className={styles.matchesSection}
    >
      {body}
    </MainSection>
  );
};

export default MatchesTab;
